<template>
  <div class="admin-container">
    <AdminSidebar :isOpen="isSidebarOpen" :userData="currentUser" />

    <div class="main-content" :class="{ 'content-shifted': isSidebarOpen }">
      <nav class="admin-nav">
        <button class="menu-btn" @click="toggleSidebar">
          <i class="fas fa-bars"></i>
        </button>
      </nav>

      <div class="admin-content">
        <div class="data-section">
          <div class="section-header">
            <h2>
              <i class="fas fa-users"></i>
              Yêu cầu đặt xe tổ chức
            </h2>
          </div>
        <!-- Filters -->
        <div class="filters-bar">
          <div class="search-box">
            <i class="fas fa-search"></i>
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Tìm theo tên, SĐT, email..."
              @input="debouncedSearch"
            />
          </div>
          <div class="status-filters">
            <button
              v-for="s in statusOptions"
              :key="s.value"
              :class="['filter-btn', { active: statusFilter === s.value }]"
              @click="setStatusFilter(s.value)"
            >
              <span class="filter-dot" :style="{ background: s.color }"></span>
              {{ s.label }}
              <span v-if="s.value && statusCounts[s.value]" class="filter-count">{{ statusCounts[s.value] }}</span>
            </button>
          </div>
        </div>

        <!-- Loading -->
        <div v-if="isLoading" class="loading-state">
          <i class="fas fa-spinner fa-spin"></i>
          Đang tải...
        </div>

        <!-- Empty state -->
        <div v-else-if="requests.length === 0" class="empty-state">
          <i class="fas fa-inbox"></i>
          <h3>Chưa có yêu cầu nào</h3>
          <p>Các yêu cầu đặt xe tổ chức từ khách hàng sẽ hiển thị ở đây</p>
        </div>

        <!-- Table -->
        <div v-else class="table-wrapper">
          <table class="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Trưởng nhóm</th>
                <th>SĐT</th>
                <th>Địa điểm</th>
                <th>Ngày đi</th>
                <th>Số TV</th>
                <th>Trạng thái</th>
                <th>Ngày tạo</th>
                <th>Thao tác</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="req in requests" :key="req.request_id" @click="openDetail(req)">
                <td class="id-cell">#{{ req.request_id }}</td>
                <td class="name-cell">
                  <strong>{{ req.leader_name }}</strong>
                  <small>{{ req.leader_email }}</small>
                </td>
                <td class="phone-cell">
                  <a :href="'tel:' + req.leader_phone" @click.stop>{{ req.leader_phone }}</a>
                </td>
                <td>{{ req.destination_location || req.destination_text || '—' }}</td>
                <td>{{ formatDate(req.travel_date) }}</td>
                <td class="count-cell">
                  <span class="member-badge">{{ req.member_count }}</span>
                </td>
                <td>
                  <span :class="['status-badge', `status-${req.status}`]">
                    {{ translateStatus(req.status) }}
                  </span>
                </td>
                <td>{{ formatDateTime(req.created_at) }}</td>
                <td class="actions-cell" @click.stop>
                  <button class="action-btn view" @click="openDetail(req)" title="Chi tiết">
                    <i class="fas fa-eye"></i>
                  </button>
                  <button class="action-btn delete" @click="confirmDelete(req)" title="Xóa">
                    <i class="fas fa-trash-alt"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Pagination -->
          <div class="pagination" v-if="totalPages > 1">
            <button
              class="page-btn"
              :disabled="currentPage === 1"
              @click="changePage(currentPage - 1)"
            >
              <i class="fas fa-chevron-left"></i>
            </button>
            <span class="page-info">Trang {{ currentPage }} / {{ totalPages }}</span>
            <button
              class="page-btn"
              :disabled="currentPage === totalPages"
              @click="changePage(currentPage + 1)"
            >
              <i class="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
        </div>
      </div>
    </div>

    <!-- Detail Modal -->
    <transition name="modal-fade">
      <div v-if="selectedRequest" class="modal-overlay" @click.self="closeDetail">
        <div class="detail-modal">
          <div class="modal-header">
            <h2>
              <i class="fas fa-clipboard-list"></i>
              Chi tiết yêu cầu #{{ selectedRequest.request_id }}
            </h2>
            <button class="close-btn" @click="closeDetail">
              <i class="fas fa-times"></i>
            </button>
          </div>

          <div class="modal-body">
            <div class="detail-grid">
              <!-- Left -->
              <div class="detail-section">
                <h3><i class="fas fa-user"></i> Thông tin trưởng nhóm</h3>
                <div class="detail-row">
                  <span class="label">Tên:</span>
                  <span class="value">{{ selectedRequest.leader_name }}</span>
                </div>
                <div class="detail-row">
                  <span class="label">SĐT:</span>
                  <span class="value">
                    <a :href="'tel:' + selectedRequest.leader_phone">{{ selectedRequest.leader_phone }}</a>
                  </span>
                </div>
                <div class="detail-row">
                  <span class="label">Email:</span>
                  <span class="value">{{ selectedRequest.leader_email }}</span>
                </div>
                <div class="detail-row" v-if="selectedRequest.notes">
                  <span class="label">Ghi chú:</span>
                  <span class="value notes-text">{{ selectedRequest.notes }}</span>
                </div>
                <div class="detail-row" v-if="selectedRequest.extra_notes">
                  <span class="label">Ghi chú thêm:</span>
                  <span class="value notes-text">{{ selectedRequest.extra_notes }}</span>
                </div>
              </div>

              <!-- Right -->
              <div class="detail-section">
                <h3><i class="fas fa-bus"></i> Thông tin chuyến đi</h3>
                <div class="detail-row">
                  <span class="label">Ngày đi:</span>
                  <span class="value">{{ formatDate(selectedRequest.travel_date) }}</span>
                </div>
                <div class="detail-row">
                  <span class="label">Địa điểm:</span>
                  <span class="value">{{ selectedRequest.destination_location || selectedRequest.destination_text || '—' }}</span>
                </div>
                <div class="detail-row">
                  <span class="label">Số thành viên:</span>
                  <span class="value"><strong>{{ selectedRequest.member_count }}</strong> người</span>
                </div>
                <div class="detail-row" v-if="hasServices(selectedRequest)">
                  <span class="label">Dịch vụ thêm:</span>
                  <span class="value">
                    <div class="service-tags">
                      <span v-if="selectedRequest.additional_services?.photo_tour" class="service-tag">📸 Photo tour</span>
                      <span v-if="selectedRequest.additional_services?.breakfast_day1" class="service-tag">🍳 Ăn sáng</span>
                      <span v-if="selectedRequest.additional_services?.lunch_day1" class="service-tag">🍜 Ăn trưa</span>
                      <span v-if="selectedRequest.additional_services?.dinner_day1" class="service-tag">🍲 Ăn tối</span>
                    </div>
                  </span>
                </div>
              </div>
            </div>

            <!-- Status + Admin notes -->
            <div class="status-section">
              <div class="status-update">
                <label>Trạng thái:</label>
                <select v-model="editStatus" class="status-select">
                  <option value="pending">Chờ xử lý</option>
                  <option value="contacted">Đã liên hệ</option>
                  <option value="confirmed">Đã xác nhận</option>
                  <option value="completed">Hoàn thành</option>
                  <option value="rejected">Từ chối</option>
                </select>
              </div>
              <div class="admin-notes-field">
                <label>Ghi chú admin:</label>
                <textarea v-model="editAdminNotes" placeholder="Thêm ghi chú nội bộ..." rows="3"></textarea>
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <button class="btn-secondary" @click="closeDetail">Đóng</button>
            <button class="btn-primary" @click="saveChanges" :disabled="isSaving">
              <span v-if="isSaving"><i class="fas fa-spinner fa-spin"></i> Đang lưu...</span>
              <span v-else><i class="fas fa-save"></i> Lưu thay đổi</span>
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Delete Confirm Modal -->
    <transition name="modal-fade">
      <div v-if="deleteTarget" class="modal-overlay" @click.self="deleteTarget = null">
        <div class="confirm-modal">
          <h3><i class="fas fa-exclamation-triangle"></i> Xác nhận xóa</h3>
          <p>Bạn có chắc muốn xóa yêu cầu #{{ deleteTarget.request_id }} của <strong>{{ deleteTarget.leader_name }}</strong>?</p>
          <div class="confirm-actions">
            <button class="btn-secondary" @click="deleteTarget = null">Hủy</button>
            <button class="btn-danger" @click="doDelete" :disabled="isDeleting">
              <span v-if="isDeleting"><i class="fas fa-spinner fa-spin"></i></span>
              <span v-else>Xóa</span>
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axiosInstance from '@/plugins/axios'
import AdminSidebar from '../common/AdminSidebar.vue'
import { ToastService } from '@/services/ToastService'
import { debounce } from 'lodash'

const isProd = process.env.VUE_APP_IS_PROD === 'true'
const API_BASE_URL = isProd
  ? process.env.VUE_APP_BACKEND_URL_PROD
  : process.env.VUE_APP_BACKEND_URL_DEV

export default {
  name: 'AdminGroupBookings',
  components: { AdminSidebar },
  data () {
    return {
      isSidebarOpen: false,
      currentUser: { full_name: 'Admin User', role: 'Administrator' },
      requests: [],
      isLoading: false,
      searchQuery: '',
      statusFilter: '',
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      perPage: 20,
      statusCounts: {},

      // Detail modal
      selectedRequest: null,
      editStatus: '',
      editAdminNotes: '',
      isSaving: false,

      // Delete modal
      deleteTarget: null,
      isDeleting: false,

      statusOptions: [
        { value: '', label: 'Tất cả', color: '#6b7280' },
        { value: 'pending', label: 'Chờ xử lý', color: '#f59e0b' },
        { value: 'contacted', label: 'Đã liên hệ', color: '#3b82f6' },
        { value: 'confirmed', label: 'Đã xác nhận', color: '#8b5cf6' },
        { value: 'completed', label: 'Hoàn thành', color: '#10b981' },
        { value: 'rejected', label: 'Từ chối', color: '#ef4444' }
      ]
    }
  },
  created () {
    this.debouncedSearch = debounce(this.fetchRequests, 400)
    const saved = localStorage.getItem('sidebarOpen')
    this.isSidebarOpen = saved === 'true'
  },
  mounted () {
    this.fetchRequests()
  },
  methods: {
    toggleSidebar () {
      this.isSidebarOpen = !this.isSidebarOpen
      localStorage.setItem('sidebarOpen', this.isSidebarOpen)
    },
    async fetchRequests () {
      this.isLoading = true
      try {
        const token = localStorage.getItem('adminToken')
        const params = {
          page: this.currentPage,
          per_page: this.perPage
        }
        if (this.statusFilter) params.status = this.statusFilter
        if (this.searchQuery) params.search = this.searchQuery

        const res = await axiosInstance.get(`${API_BASE_URL}/admin/group-booking-requests/`, {
          headers: { Authorization: `Bearer ${token}` },
          params
        })

        this.requests = res.data.items || []
        this.totalPages = res.data.pages || 1
        this.totalItems = res.data.total || 0
      } catch (err) {
        console.error('Error fetching group bookings:', err)
        if (err.response?.status === 401) {
          sessionStorage.setItem('session_expired_toast', '1')
          this.$router.push('/login')
        }
        ToastService.error('Lỗi tải danh sách yêu cầu')
      } finally {
        this.isLoading = false
      }
    },
    setStatusFilter (val) {
      this.statusFilter = val
      this.currentPage = 1
      this.fetchRequests()
    },
    changePage (page) {
      this.currentPage = page
      this.fetchRequests()
    },
    openDetail (req) {
      this.selectedRequest = { ...req }
      this.editStatus = req.status
      this.editAdminNotes = req.admin_notes || ''
    },
    closeDetail () {
      this.selectedRequest = null
    },
    async saveChanges () {
      this.isSaving = true
      try {
        const token = localStorage.getItem('adminToken')
        await axiosInstance.put(
          `${API_BASE_URL}/admin/group-booking-requests/${this.selectedRequest.request_id}`,
          {
            status: this.editStatus,
            admin_notes: this.editAdminNotes
          },
          { headers: { Authorization: `Bearer ${token}` } }
        )
        ToastService.success('Cập nhật thành công')
        this.closeDetail()
        this.fetchRequests()
      } catch (err) {
        console.error('Error updating:', err)
        ToastService.error('Lỗi cập nhật')
      } finally {
        this.isSaving = false
      }
    },
    confirmDelete (req) {
      this.deleteTarget = req
    },
    async doDelete () {
      this.isDeleting = true
      try {
        const token = localStorage.getItem('adminToken')
        await axiosInstance.delete(
          `${API_BASE_URL}/admin/group-booking-requests/${this.deleteTarget.request_id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        )
        ToastService.success('Đã xóa yêu cầu')
        this.deleteTarget = null
        this.fetchRequests()
      } catch (err) {
        console.error('Error deleting:', err)
        ToastService.error('Lỗi khi xóa')
      } finally {
        this.isDeleting = false
      }
    },
    hasServices (req) {
      const s = req.additional_services
      return s && (s.photo_tour || s.breakfast_day1 || s.lunch_day1 || s.dinner_day1)
    },
    translateStatus (status) {
      const map = {
        pending: 'Chờ xử lý',
        contacted: 'Đã liên hệ',
        confirmed: 'Đã xác nhận',
        completed: 'Hoàn thành',
        rejected: 'Từ chối'
      }
      return map[status] || status
    },
    formatDate (date) {
      if (!date) return '—'
      return new Date(date).toLocaleDateString('vi-VN', {
        day: '2-digit', month: '2-digit', year: 'numeric'
      })
    },
    formatDateTime (date) {
      if (!date) return '—'
      return new Date(date).toLocaleDateString('vi-VN', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.admin-content {
  padding: 1.5rem;
}

/* Filters */
.filters-bar {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.search-box {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: white;
  border: 1.5px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 0.6rem 1rem;
  max-width: 400px;
}

.search-box i {
  color: #9ca3af;
}

.search-box input {
  border: none;
  outline: none;
  font-size: 0.875rem;
  flex: 1;
  font-family: inherit;
}

.status-filters {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.filter-btn {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.45rem 0.9rem;
  border: 1.5px solid #e5e7eb;
  border-radius: 2rem;
  background: white;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  color: #4b5563;
  font-family: inherit;
}

.filter-btn:hover {
  border-color: #10b981;
  color: #059669;
}

.filter-btn.active {
  background: #ecfdf5;
  border-color: #10b981;
  color: #059669;
  font-weight: 600;
}

.filter-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.filter-count {
  background: #e5e7eb;
  padding: 0 0.4rem;
  border-radius: 1rem;
  font-size: 0.72rem;
  font-weight: 600;
}

.filter-btn.active .filter-count {
  background: #d1fae5;
}

/* Table */
.table-wrapper {
  background: white;
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table thead {
  background: #f9fafb;
}

.data-table th {
  padding: 0.75rem 1rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-align: left;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #e5e7eb;
}

.data-table td {
  padding: 0.75rem 1rem;
  font-size: 0.85rem;
  color: #374151;
  border-bottom: 1px solid #f3f4f6;
}

.data-table tbody tr {
  cursor: pointer;
  transition: background 0.15s;
}

.data-table tbody tr:hover {
  background: #f0fdf4;
}

.id-cell {
  font-weight: 700;
  color: #10b981;
  font-size: 0.82rem;
}

.name-cell {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.name-cell strong {
  font-size: 0.85rem;
}

.name-cell small {
  color: #9ca3af;
  font-size: 0.75rem;
}

.phone-cell a {
  color: #3b82f6;
  text-decoration: none;
  font-weight: 500;
}

.phone-cell a:hover {
  text-decoration: underline;
}

.member-badge {
  background: #ecfdf5;
  color: #059669;
  padding: 0.2rem 0.6rem;
  border-radius: 1rem;
  font-weight: 700;
  font-size: 0.82rem;
}

.count-cell {
  text-align: center;
}

/* Status badges */
.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.7rem;
  border-radius: 2rem;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}

.status-pending {
  background: #fef3c7;
  color: #92400e;
}

.status-contacted {
  background: #dbeafe;
  color: #1e40af;
}

.status-confirmed {
  background: #ede9fe;
  color: #5b21b6;
}

.status-completed {
  background: #d1fae5;
  color: #065f46;
}

.status-rejected {
  background: #fee2e2;
  color: #991b1b;
}

/* Actions */
.actions-cell {
  display: flex;
  gap: 0.4rem;
}

.action-btn {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  transition: all 0.2s;
}

.action-btn.view {
  background: #ecfdf5;
  color: #10b981;
}

.action-btn.view:hover {
  background: #d1fae5;
}

.action-btn.delete {
  background: #fef2f2;
  color: #ef4444;
}

.action-btn.delete:hover {
  background: #fee2e2;
}

/* Pagination */
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 1rem;
  border-top: 1px solid #f3f4f6;
}

.page-btn {
  background: white;
  border: 1.5px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  color: #374151;
  transition: all 0.2s;
}

.page-btn:hover:not(:disabled) {
  border-color: #10b981;
  color: #10b981;
}

.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-info {
  font-size: 0.85rem;
  color: #6b7280;
  font-weight: 500;
}

/* Loading & Empty */
.loading-state,
.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  color: #9ca3af;
}

.loading-state i,
.empty-state i {
  font-size: 2.5rem;
  margin-bottom: 1rem;
  display: block;
}

.empty-state h3 {
  color: #6b7280;
  margin: 0 0 0.5rem;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.45);
  backdrop-filter: blur(3px);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.detail-modal {
  background: white;
  border-radius: 1rem;
  width: 100%;
  max-width: 750px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 50px rgba(0,0,0,0.15);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.modal-header h2 {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.modal-header h2 i {
  color: #10b981;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.1rem;
  color: #9ca3af;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.5rem;
}

.close-btn:hover {
  background: #f3f4f6;
  color: #374151;
}

.modal-body {
  padding: 1.5rem;
}

.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.detail-section h3 {
  font-size: 0.9rem;
  font-weight: 700;
  color: #064e3b;
  margin: 0 0 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.detail-section h3 i {
  color: #10b981;
}

.detail-row {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.6rem;
  font-size: 0.85rem;
}

.detail-row .label {
  font-weight: 600;
  color: #6b7280;
  min-width: 80px;
  flex-shrink: 0;
}

.detail-row .value {
  color: #1f2937;
}

.detail-row .value a {
  color: #3b82f6;
  text-decoration: none;
}

.notes-text {
  white-space: pre-wrap;
  background: #f9fafb;
  padding: 0.4rem 0.6rem;
  border-radius: 0.375rem;
  font-size: 0.82rem;
  line-height: 1.5;
}

.service-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.service-tag {
  background: #ecfdf5;
  color: #059669;
  padding: 0.2rem 0.5rem;
  border-radius: 0.375rem;
  font-size: 0.78rem;
  font-weight: 500;
}

/* Status section */
.status-section {
  background: #f9fafb;
  border-radius: 0.75rem;
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.status-update {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.status-update label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #374151;
  min-width: 80px;
}

.status-select {
  padding: 0.5rem 0.75rem;
  border: 1.5px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 0.85rem;
  font-family: inherit;
  cursor: pointer;
  background: white;
  min-width: 160px;
}

.status-select:focus {
  outline: none;
  border-color: #10b981;
}

.admin-notes-field label {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.35rem;
}

.admin-notes-field textarea {
  width: 100%;
  padding: 0.6rem 0.75rem;
  border: 1.5px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 0.85rem;
  font-family: inherit;
  resize: vertical;
  background: white;
}

.admin-notes-field textarea:focus {
  outline: none;
  border-color: #10b981;
}

/* Footer */
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
}

.btn-secondary,
.btn-primary,
.btn-danger {
  padding: 0.55rem 1.25rem;
  border-radius: 0.5rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  transition: all 0.2s;
  font-family: inherit;
  border: none;
}

.btn-secondary {
  background: #f3f4f6;
  color: #4b5563;
}

.btn-secondary:hover {
  background: #e5e7eb;
}

.btn-primary {
  background: #10b981;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #059669;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-danger {
  background: #ef4444;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background: #dc2626;
}

/* Confirm modal */
.confirm-modal {
  background: white;
  border-radius: 1rem;
  padding: 1.5rem;
  max-width: 400px;
  width: 100%;
  text-align: center;
}

.confirm-modal h3 {
  color: #ef4444;
  margin: 0 0 0.75rem;
  font-size: 1.05rem;
}

.confirm-modal p {
  color: #6b7280;
  font-size: 0.9rem;
  margin: 0 0 1.25rem;
}

.confirm-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: center;
}

/* Transitions */
.modal-fade-enter-active { transition: opacity 0.3s; }
.modal-fade-leave-active { transition: opacity 0.2s; }
.modal-fade-enter-from,
.modal-fade-leave-to { opacity: 0; }

/* Responsive */
@media (max-width: 768px) {
  .admin-content {
    padding: 1rem;
  }

  .detail-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .data-table {
    font-size: 0.78rem;
  }

  .data-table th,
  .data-table td {
    padding: 0.5rem 0.6rem;
  }

  .status-update {
    flex-direction: column;
    align-items: flex-start;
  }

  .search-box {
    max-width: 100%;
  }
}
</style>
